import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  TextField,
  ThemeProvider,
  createTheme,
  styled,
  Autocomplete,
} from "@mui/material";
import { Dictionary } from "../../types/Dictionary";
import { reloadOptions } from "../../request/api";
import { sendLoadingPostRequest } from "../../request/request";
import { DictionaryCombo } from "../../types/DictionaryCombo";
import { BeneficiaryItem } from "./BeneficiaryItem";
import { IBeneficiaryItem } from "../../types/IBeneficiaryItem";
import FormContext from "../../contexts/FormContext";

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 40,
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(1) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

interface IBeneficiaryProps {
  errors?: string[];
  setErrors: React.Dispatch<React.SetStateAction<string[] | undefined>>;
}

const Beneficiary = ({ errors, setErrors }: IBeneficiaryProps) => {
  const context = useContext(FormContext);
  const [withoutBeneficiary, setWithoutBeneficiary] = useState<boolean>(false);
  const [withBeneficiary, setWithBeneficiary] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dictionary, setDictionary] = useState<Dictionary>();
  const [firstBeneficiary, setFirstBeneficiary] = useState<IBeneficiaryItem>();
  const [secondBeneficiary, setSecondBeneficiary] =
    useState<IBeneficiaryItem>();
  const [showSecondBeneficiary, setShowSecondBeneficiary] = useState<boolean>();
  const [thirdBeneficiary, setThirdBeneficiary] = useState<IBeneficiaryItem>();
  const [showThirdBeneficiary, setShowThirdBeneficiary] = useState<boolean>();

  const getDictionary = async () => {
    reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      Dictionary
    >({
      loadingFunction: setLoading,
      endpoint: "Dictionary/GetAll",
    });
    if (success && response?.data) {
      setDictionary(response?.data);
    }
  };

  const deleteSecondBeneficiary = () => {
    if (thirdBeneficiary != null) {
      setSecondBeneficiary({ ...thirdBeneficiary });
      setShowSecondBeneficiary(true);
      setShowThirdBeneficiary(false);
      setThirdBeneficiary(undefined);
    } else {
      setSecondBeneficiary(undefined);
      setShowSecondBeneficiary(false);
    }
  };

  const deleteFirstBeneficiary = () => {
    if (thirdBeneficiary != null) {
      setFirstBeneficiary(secondBeneficiary);
      setSecondBeneficiary(thirdBeneficiary);
      setThirdBeneficiary(undefined);
      setShowThirdBeneficiary(false);
      setShowSecondBeneficiary(true);
    } else if (secondBeneficiary != null) {
      setFirstBeneficiary(secondBeneficiary);
      setSecondBeneficiary(undefined);
      setShowThirdBeneficiary(false);
      setShowSecondBeneficiary(false);
    } else {
      setFirstBeneficiary(undefined);
    }
  };

  const deleteBeneficiary = (number: number) => {
    if (number === 1) {
      deleteFirstBeneficiary();
    } else if (number === 2) {
      deleteSecondBeneficiary();
    } else {
      setThirdBeneficiary(undefined);
      setShowThirdBeneficiary(false);
    }
  };

  useEffect(() => {
    getDictionary();
    if (context.ppp != null) {
      setFirstBeneficiary(context.ppp.firstBeneficiary);
      setSecondBeneficiary(context.ppp.secondBeneficiary);
      setShowSecondBeneficiary(context.ppp.secondBeneficiary != null);
      setThirdBeneficiary(context.ppp.thirdBeneficiary);
      setShowThirdBeneficiary(context.ppp.thirdBeneficiary != null);
      if (context.ppp.withBeneficiary != null) {
        if (context.ppp.withBeneficiary === true) {
          setWithBeneficiary(true);
        } else {
          setWithoutBeneficiary(true);
        }
      }
    }
  }, []);

  useMemo(() => {
    if (context.setPPP) {
      let beneficiary: any = undefined;

      if (withBeneficiary === true) {
        beneficiary = true;
        setErrors((prev) => {
          if (prev?.indexOf("main") !== -1) {
            return prev?.filter((item) => item !== "main");
          }
          return prev;
        });
      } else if (withoutBeneficiary === true) {
        beneficiary = false;
        setErrors((prev) => {
          if (prev?.indexOf("main") !== -1) {
            return prev?.filter((item) => item !== "main");
          }
          return prev;
        });
      }

      if (firstBeneficiary) {
        if (
          firstBeneficiary.firstName &&
          firstBeneficiary.firstName.length > 0
        ) {
          setErrors((prev) => {
            if (prev?.indexOf("firstFirstName") !== -1) {
              return prev?.filter((item) => item !== "firstFirstName");
            }
            return prev;
          });
        }
        if (firstBeneficiary.lastName && firstBeneficiary.lastName.length > 0) {
          setErrors((prev) => {
            if (prev?.indexOf("firstLastName") !== -1) {
              return prev?.filter((item) => item !== "firstLastName");
            }
            return prev;
          });
        }

        if (firstBeneficiary.option) {
          setErrors((prev) => {
            if (prev?.indexOf("firstOption") !== -1) {
              return prev?.filter((item) => item !== "firstOption");
            }
            return prev;
          });
        }
      }

      if (secondBeneficiary) {
        if (
          secondBeneficiary.firstName &&
          secondBeneficiary.firstName.length > 0
        ) {
          setErrors((prev) => {
            if (prev?.indexOf("secondFirstName") !== -1) {
              return prev?.filter((item) => item !== "secondFirstName");
            }
            return prev;
          });
        }

        if (
          secondBeneficiary.lastName &&
          secondBeneficiary.lastName.length > 0
        ) {
          setErrors((prev) => {
            if (prev?.indexOf("secondLastName") !== -1) {
              return prev?.filter((item) => item !== "secondLastName");
            }
            return prev;
          });
        }

        if (secondBeneficiary.option) {
          setErrors((prev) => {
            if (prev?.indexOf("secondOption") !== -1) {
              return prev?.filter((item) => item !== "secondOption");
            }
            return prev;
          });
        }
      }

      if (thirdBeneficiary) {
        if (
          thirdBeneficiary.firstName &&
          thirdBeneficiary.firstName.length > 0
        ) {
          setErrors((prev) => {
            if (prev?.indexOf("thirdFirstName") !== -1) {
              return prev?.filter((item) => item !== "thirdFirstName");
            }
            return prev;
          });
        }

        if (thirdBeneficiary.lastName && thirdBeneficiary.lastName.length > 0) {
          setErrors((prev) => {
            if (prev?.indexOf("thirdLastName") !== -1) {
              return prev?.filter((item) => item !== "thirdLastName");
            }
            return prev;
          });
        }

        if (thirdBeneficiary.option) {
          setErrors((prev) => {
            if (prev?.indexOf("thirdOption") !== -1) {
              return prev?.filter((item) => item !== "thirdOption");
            }
            return prev;
          });
        }
      }

      context.setPPP((prev) => {
        return {
          ...prev,
          firstBeneficiary,
          secondBeneficiary,
          thirdBeneficiary,
          withBeneficiary: beneficiary,
        };
      });
    }
  }, [
    firstBeneficiary,
    secondBeneficiary,
    thirdBeneficiary,
    withoutBeneficiary,
    withBeneficiary,
  ]);

  return (
    <>
      <hr className="hrMargin" />

      <p className="bold red section">Beneficjent rzeczywisty</p>
      <div className="row px-3 mt-2">
        <div
          onClick={() => {
            setWithoutBeneficiary(true);
            setWithBeneficiary(false);
            setFirstBeneficiary(undefined);
            setSecondBeneficiary(undefined);
            setShowSecondBeneficiary(false);
            setThirdBeneficiary(undefined);
            setShowThirdBeneficiary(false);
          }}
          className="custom-control custom-checkbox"
        >
          <input
            type="checkbox"
            className="custom-control-input"
            checked={withoutBeneficiary}
          />
          <label className="custom-control-label">
            Oświadczam, że nie mam beneficjenta rzeczywistego.
          </label>
        </div>
      </div>

      <div className="row px-3 mb-3">
        <div
          onClick={() => {
            setWithBeneficiary(true);
            setWithoutBeneficiary(false);
          }}
          className="custom-control custom-checkbox"
        >
          <input
            type="checkbox"
            className="custom-control-input"
            checked={withBeneficiary}
          />
          <label className="custom-control-label">
            Oświadczam, że moim beneficjentem rzeczywistym jest:
          </label>
        </div>
      </div>

      {errors && errors.indexOf("main") !== -1 ? (
        <span className="validationError" role="alert">
          <i className="fa fa-exclamation-circle" />
          To pole jest wymagane
        </span>
      ) : null}

      {withBeneficiary ? (
        <>
          <BeneficiaryItem
            key={"firstBeneficiary"}
            dictionary={dictionary}
            errors={errors ?? []}
            setErrors={setErrors}
            item={firstBeneficiary}
            deleteItem={() => deleteBeneficiary(1)}
            setItem={setFirstBeneficiary}
            setShow={setShowSecondBeneficiary}
            show={secondBeneficiary == null}
          />
        </>
      ) : null}

      {withBeneficiary && showSecondBeneficiary ? (
        <>
          <BeneficiaryItem
            key={"secondBeneficiary"}
            dictionary={dictionary}
            errors={errors ?? []}
            setErrors={setErrors}
            item={secondBeneficiary}
            deleteItem={() => deleteBeneficiary(2)}
            setItem={setSecondBeneficiary}
            setShow={setShowThirdBeneficiary}
            hide={setShowSecondBeneficiary}
            number={2}
            show={thirdBeneficiary == null}
          />
        </>
      ) : null}

      {withBeneficiary && showThirdBeneficiary ? (
        <>
          <BeneficiaryItem
            key={"thirdBeneficiary"}
            dictionary={dictionary}
            errors={errors ?? []}
            setErrors={setErrors}
            item={thirdBeneficiary}
            deleteItem={() => deleteBeneficiary(3)}
            setItem={setThirdBeneficiary}
            number={3}
            hide={setShowThirdBeneficiary}
            show={false}
          />
        </>
      ) : null}
    </>
  );
};

export { Beneficiary };
